import React, { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

interface VirtualChatListProps<T> {
  className: string;
  items: T[];
  getItemKey: (item: T, index: number) => React.Key;
  renderItem: (item: T, index: number) => React.ReactNode;
  estimateSize: number | ((index: number) => number);
  forceScrollToBottom?: boolean;
  rowGap?: number;
  overscan?: number;
}

const VirtualChatList = <T,>({
  className,
  items,
  getItemKey,
  renderItem,
  estimateSize,
  forceScrollToBottom = false,
  rowGap = 16,
  overscan = 12,
}: VirtualChatListProps<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => typeof estimateSize === 'function' ? estimateSize(index) : estimateSize,
    getItemKey: (index) => getItemKey(items[index], index),
    overscan,
  });

  const scrollToBottom = () => {
    if (items.length === 0) return;
    requestAnimationFrame(() => {
      virtualizer.scrollToIndex(items.length - 1, { align: 'end' });
    });
  };

  useEffect(() => {
    if (forceScrollToBottom || isAtBottomRef.current) {
      scrollToBottom();
    }
  }, [forceScrollToBottom, items]);

  // 아이템 내용이 타이핑 애니메이션 등으로 자라나 items 변경 없이 전체 높이만 커질 때도
  // 하단 고정 유지 (measureElement 재측정 → totalSize 변경으로 감지)
  const totalSize = virtualizer.getTotalSize();
  useEffect(() => {
    if (!isAtBottomRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [totalSize]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    isAtBottomRef.current = target.scrollHeight - target.scrollTop - target.clientHeight < 40;
  };

  return (
    <div className={className} ref={scrollRef} onScroll={handleScroll}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = items[virtualItem.index];
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                boxSizing: 'border-box',
                left: 0,
                paddingTop: virtualItem.index === 0 ? 0 : rowGap,
                position: 'absolute',
                top: 0,
                transform: `translateY(${virtualItem.start}px)`,
                width: '100%',
              }}
            >
              {renderItem(item, virtualItem.index)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualChatList;